import { useEffect, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { auth, getErrorMessage } from '@/lib/platform';
import { useContactPhone } from '@/lib/contact';

import { ticketsApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import {
  formatDateTimeFull,
  formatMoney,
  orderStatusLabel,
  ticketStatusLabel,
  ticketTypeLabel,
} from '@/lib/format';
import { metroTerms } from '@/lib/metro-terms';
import { cancellationNote, operatorLabel, refundEta, renderTerms } from '@/lib/operator-terms';
import { displayRefund } from '@/lib/cancel-refund';
import { getTicketCancelState, isBusTicket } from '@/lib/ticket-kind';
import type { Ticket, TicketPass } from '@/lib/types';
import { ErrorState } from '@/components/shared/error-state';
import { InfoNote } from '@/components/shared/info-note';
import { ReportProblemModal, type ReportCategory } from '@/components/shared/report-problem-modal';
import { Skeleton } from '@/components/shared/skeleton';
import { DynamicQRCode } from '@/components/dynamic-qr';
import { Screen } from '@/components/chrome';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/ui/status-badge';
import { Spinner } from '@/components/ui/spinner';
import { QrFrame } from '@/components/ui/qr-frame';
import { Icon } from '@/components/ui/icon';
import { SumRow } from '@/components/ui/sum-row';
import { Money } from '@/components/ui/money';
import { Accordion } from '@/components/ui/accordion';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { JourneyLine } from '@/components/ui/journey-line';

// Buyer(BAP)-raisable IGM grievances for metro + intra-city bus, per the ONDC "Unreserved
// Ticketing - IGM Category and Sub Category" sheet (ondc/cert/reference/igm/). Metro AND bus both
// run IGM 1.0.0 live — the TRV11-wide default (QA-confirmed, ONWGR-008918) with the transit
// taxonomy — so they share this picker. Only ORD101 + PMT101/102 are BAP-raisable and rider-facing
// — ALL fulfillment codes (FLM101-104: gate-scan, cancel, modify, to&fro) are BPP-raised, and
// PMT103-106 are settlement (RSF)/BPP. The backend derives the parent category.
const TRANSIT_ISSUE_CATEGORIES: ReportCategory[] = [
  { code: 'ORD101', label: 'Ticket not generated after payment' },
  { code: 'PMT101', label: 'Refund not received' },
  { code: 'PMT102', label: 'Amount deducted but payment failed' },
];

export const Route = createFileRoute('/_authed/metro/$id')({
  component: TicketDetailPage,
});

type CancelStage = 'none' | 'soft' | 'preview' | 'confirm' | 'abandon' | 'done';
type ChangeDestStage = 'none' | 'form' | 'soft' | 'preview' | 'confirm' | 'done';

function TicketDetailPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();

  const [cancelStage, setCancelStage] = useState<CancelStage>('none');
  const [cancelLeg, setCancelLeg] = useState<string | undefined>(undefined);
  const [cancelError, setCancelError] = useState('');
  const [reportOpen, setReportOpen] = useState(false);
  const [contactPhone] = useContactPhone();

  // Change-destination (TRV11 2.0.1 end-stop change, bus-only). SOFT (re-quote) →
  // CONFIRM. The PAYMENT leg is not wired here (no payment data on the Ticket DTO).
  const [cdStage, setCdStage] = useState<ChangeDestStage>('none');
  const [cdName, setCdName] = useState('');
  const [cdCode, setCdCode] = useState('');
  const [cdGps, setCdGps] = useState('');
  const [cdError, setCdError] = useState('');
  // Confirm-vehicle (TRV11 2.0.1 bus confirmation update, bus-only).
  const [cvOpen, setCvOpen] = useState(false);
  const [cvReg, setCvReg] = useState('');
  const [cvError, setCvError] = useState('');

  const awaiting =
    cancelStage === 'soft' ||
    cancelStage === 'confirm' ||
    cancelStage === 'abandon' ||
    cdStage === 'soft' ||
    cdStage === 'confirm';

  const ticketQuery = useQuery({
    queryKey: queryKeys.detail(id),
    queryFn: () => ticketsApi.ticket(id),
    refetchInterval: (q) => {
      const t = q.state.data;
      if (!t) return false;
      if (awaiting) return 1500;
      if (t.status === 'ACTIVE' && t.passes.some((p) => p.ticketStatus === 'UNCLAIMED')) {
        return 5000;
      }
      return false;
    },
  });

  const ticket = ticketQuery.data;
  // serverClock − clientReceiptClock: lets a dynamic-QR pass offset-correct the
  // device clock when minting the gate timestamp. dataUpdatedAt is the receipt
  // time of the latest poll, paired with the response's serverTimeMs.
  const qrOffsetMs =
    ticket?.serverTimeMs && ticketQuery.dataUpdatedAt
      ? ticket.serverTimeMs - ticketQuery.dataUpdatedAt
      : 0;

  const cancelMut = useMutation({
    mutationFn: (vars: { stage: 'SOFT' | 'CONFIRM' | 'ABANDON'; fulfillmentId?: string; reason?: string }) =>
      ticketsApi.cancel(id, vars),
  });

  // Bus-only post-confirm flows (TRV11 2.0.1). Metro runs 2.0.0 and the backend rejects
  // these (ErrUpdateStopsNotSupported), so gate them on the vehicle category.
  const isBus = isBusTicket(ticket);
  const fulfillmentId = ticket?.passes[0]?.fulfillmentId ?? '';

  const changeDestMut = useMutation({
    mutationFn: (stage: 'SOFT' | 'CONFIRM') =>
      ticketsApi.changeDestination(id, {
        fulfillmentId,
        stage,
        endStopName: cdName.trim() || undefined,
        endStopCode: cdCode.trim() || undefined,
        endStopGps: cdGps.trim() || undefined,
      }),
  });
  const confirmVehicleMut = useMutation({
    mutationFn: (vars: { vehicleRegistration?: string; claimAuthorization?: boolean }) =>
      ticketsApi.confirmVehicle(id, { fulfillmentId, ...vars }),
  });

  function requestNewFare() {
    if (!cdName.trim() && !cdCode.trim()) return;
    setCdError('');
    setCdStage('soft');
    changeDestMut.mutate('SOFT', {
      onSuccess: () => {
        void ticketQuery.refetch();
        setCdStage('preview');
      },
      onError: (e) => {
        setCdError(cancelErrorMessage(e));
        setCdStage('form');
      },
    });
  }
  function confirmChangeDest() {
    setCdStage('confirm');
    changeDestMut.mutate('CONFIRM', {
      onSuccess: () => {
        void ticketQuery.refetch();
        setCdStage('done');
      },
      onError: (e) => {
        setCdError(cancelErrorMessage(e));
        setCdStage('preview');
      },
    });
  }
  async function submitConfirmVehicle(vars: {
    vehicleRegistration?: string;
    claimAuthorization?: boolean;
  }) {
    setCvError('');
    try {
      await confirmVehicleMut.mutateAsync(vars);
      setCvOpen(false);
      setCvReg('');
      void ticketQuery.refetch();
    } catch (e) {
      setCvError(cancelErrorMessage(e));
    }
  }

  // Advance the 2-stage cancel as the order status lands via polling. Keyed off `cancellation.state`,
  // NOT the top-level `ticket.status`: a SOFT preview no longer degrades `status` (the backend folds
  // SOFT_CANCEL into ACTIVE so a preview never poisons the ticket's usability), and drives the state
  // machine through `cancellation.state` instead — 'quoted' (preview ready) → 'cancelled' (terminal).
  useEffect(() => {
    if (!ticket) return;
    const c = ticket.cancellation;
    const busSoftPreview =
      isBus && (ticket.status === 'SOFT_CANCEL' || c?.status === 'SOFT_CANCEL');
    const softPreviewActive = busSoftPreview || c?.state === 'quoted';

    // Bus tickets expose the real TRV11 2.0.1 SOFT_CANCEL status. Keep that order in the quote
    // preview until the rider explicitly sends CONFIRM_CANCEL. `c.state === 'quoted'` remains the
    // existing normalized path for metro and newer backend responses.
    if ((cancelStage === 'none' || cancelStage === 'soft') && softPreviewActive) {
      setCancelStage('preview');
      return;
    }
    // The operator rejected the cancel (e.g. cancellation window closed) — surface the real reason
    // immediately instead of spinning until the 35s timeout. The ticket stays valid, so a SOFT
    // rejection returns to 'none' (retryable) and a CONFIRM rejection returns to the 'preview'.
    if ((cancelStage === 'soft' || cancelStage === 'confirm') && c?.state === 'failed') {
      setCancelError(
        c.errorMessage || 'The operator could not cancel this ticket. Please try again.'
      );
      setCancelStage(cancelStage === 'soft' ? 'none' : 'preview');
      return;
    }
    if (
      cancelStage === 'confirm' &&
      (c?.state === 'partial' ||
        c?.state === 'cancelled' ||
        c?.status === 'CANCELLED' ||
        ticket.status === 'CANCELLED')
    ) {
      setCancelStage('done');
    }
  }, [ticket, cancelStage, isBus]);

  // Don't spin forever — but the two legs have very different service levels, and one timeout for
  // both cried wolf on every healthy BMRCL cancellation.
  //
  //   SOFT is a quote. It is one request/callback bounded by the Beckn TTL (PT30S), so ~35s is right.
  //
  //   CONFIRM is not. Under TRV11 2.0.1 the operator may answer CANCEL_INITIATED — "accepted, not yet
  //   finalised" — and the backend then waits on its cancel-convergence sweep, which force-resolves at
  //   DeadlineSecs=300 (internal/features/ondc/application/reconcile_cancels.go). Timing out at 35s
  //   told the rider "taking longer than expected" on a cancellation that was proceeding exactly as
  //   designed and would land normally. Observed both ends of that range on real bookings: prod BMRCL
  //   finalises via on_update in ~2s, preprod only ever reaches CANCELLED via the 300s force-resolve.
  //
  // So the confirm budget must exceed the backend's own deadline, or the UI gives up while the server
  // is still working. The margin covers the sweep's poll interval plus a round trip.
  const cancelTimeoutMs = cancelStage === 'soft' ? 35_000 : 330_000;
  useEffect(() => {
    if (cancelStage !== 'soft' && cancelStage !== 'confirm') return;
    const timer = setTimeout(() => {
      setCancelError(
        cancelStage === 'soft'
          ? 'The operator is taking longer than expected to calculate your refund. Please try again.'
          : 'Cancellation is taking longer than expected. Check My Trips shortly, or try again.'
      );
      // A SOFT that never produced a quote may still have landed on the backend as SOFT_CANCEL;
      // abandon it so a timed-out preview never strands the ticket. (CONFIRM is committing — don't.)
      if (cancelStage === 'soft') {
        abandonCancelPreview();
        return;
      }
      setCancelStage('preview');
    }, cancelTimeoutMs);
    return () => clearTimeout(timer);
    // Arm the timeout once per stage transition; cancelMut/cancelLeg are stable for the flow's
    // lifetime and must not re-arm the timer on every render. cancelTimeoutMs is derived from
    // cancelStage, so it changes only when the stage does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cancelStage]);

  function startCancel(leg?: string) {
    setCancelError('');
    setCancelLeg(leg);
    setCancelStage('soft');
    cancelMut.mutate(
      { stage: 'SOFT', fulfillmentId: leg, reason: '001' },
      {
        onError: (e) => {
          setCancelError(cancelErrorMessage(e));
          setCancelStage('none');
        },
      }
    );
  }

  function confirmCancel() {
    setCancelStage('confirm');
    cancelMut.mutate(
      { stage: 'CONFIRM', fulfillmentId: cancelLeg, reason: '001' },
      {
        onError: (e) => {
          setCancelError(cancelErrorMessage(e));
          setCancelStage('preview');
        },
      }
    );
  }

  function abandonCancelPreview() {
    setCancelStage('abandon');
    cancelMut.mutate(
      { stage: 'ABANDON', fulfillmentId: cancelLeg },
      {
        onSuccess: async () => {
          await ticketQuery.refetch();
          setCancelStage('none');
        },
        onError: (e) => {
          setCancelError(cancelErrorMessage(e));
          setCancelStage('preview');
        },
      }
    );
  }

  // Backing out of a cancel (tap "Keep ticket", dismiss the preview, or a SOFT that timed out) must
  // never leave the order stranded in SOFT_CANCEL — the trap that stripped the QR and hid the Cancel
  // button. If a SOFT preview was created on the backend, fire ABANDON to revert it to ACTIVE (a
  // no-op if nothing was created; the backend also auto-expires an abandoned preview ~10 min later).
  function keepTicket() {
    const created = cancelStage === 'preview' || ticket?.cancellation?.state === 'quoted';
    setCancelError('');
    if (created) {
      abandonCancelPreview();
      return;
    }
    setCancelStage('none');
  }

  const accent: 'bus' | 'metro' = isBus ? 'bus' : 'metro';
  const back = () => void navigate({ to: '/trips' });

  if (ticketQuery.isLoading) {
    return (
      <Screen title="Ticket" onBack={back} accent={accent}>
        <div className="wb-col wb-gap-16">
          <Skeleton w="60%" h={24} />
          <Skeleton w="100%" h={320} r={22} />
        </div>
      </Screen>
    );
  }
  if (ticketQuery.isError || !ticket) {
    return (
      <Screen title="Ticket" onBack={back} accent={accent}>
        <ErrorState error={ticketQuery.error} onRetry={() => void ticketQuery.refetch()} />
      </Screen>
    );
  }

  const passengerCount = ticket.passengerCount ?? 1;
  // A ticket shows more than one QR (pass) in two very different situations, and they must be
  // labelled differently: a round trip (RJT) has genuine onward/return LEGS, whereas any other
  // multi-pass ticket is one QR PER PASSENGER (e.g. DMRC issues a distinct QR per rider; BMRCL/CMRL
  // instead share one QR and the backend collapses to a single pass). Keying on the ticket type
  // matches the backend's collapse rule and is reliable even when the payload carries no leg stops.
  const passLabelMode: 'leg' | 'passenger' = ticket.ticketType === 'RJT' ? 'leg' : 'passenger';
  const validUntil = ticket.passes.find((p) => p.validUntil)?.validUntil;
  const ticketNumbers = ticket.passes
    .map((p) => p.ticketNumber)
    .filter(Boolean)
    .join(', ');
  // Keyed off `cancellation.state`, not `status`: a SOFT preview keeps the ticket ACTIVE, so the
  // "don't use this ticket" banner appears only once a CONFIRM is genuinely in flight ('processing')
  // — never during the refund preview. ('quoted' → no banner; the QR and actions stay live.)
  const cancelState = getTicketCancelState(ticket);
  const isCancelling = cancelState === 'processing';
  const isCancelled = cancelState === 'cancelled';
  const isPartiallyCancelled = cancelState === 'partial';
  const isActive = ticket.status === 'ACTIVE';
  const cancelQuoted = cancelState === 'quoted';
  const canStartCancel =
    ticket.cancellable && cancelStage === 'none' && !cancelQuoted && !isCancelling;
  // The operator whose rules apply, resolved server-side. Absent only against a backend
  // older than the field.
  const operator = ticket.operator;
  // LEGACY FALLBACK, deliberately retained for one release. `qrDynamic` is a QR-format flag
  // standing in for operator identity — the very conflation that showed BMRCL's rules to
  // Kochi riders. It is only reachable when `operator` is missing, i.e. an old backend, and
  // blanking the terms there would be a regression for Bangalore. Delete once every
  // deployed backend serves `operator`.
  const isBmrcl = ticket.passes.some((p) => p.qrDynamic);
  const terms = operator ? renderTerms(operator) : metroTerms(isBmrcl);
  const fareLines = buildFareLines(ticket, passengerCount);

  return (
    <Screen title="Ticket" onBack={back} accent={accent}>
      <div className="wb-col wb-gap-16">
        {/* Cancellation status */}
        {(isCancelling || isCancelled) && <CancelStatusBanner ticket={ticket} />}
        {isPartiallyCancelled && (
          <PartialCancelStatusBanner ticket={ticket} passengerCount={passengerCount} />
        )}

        {/* Boarding pass */}
        <div className="wb-card wb-card--pad wb-col wb-gap-16">
          <div className="wb-row wb-between" style={{ alignItems: 'flex-start' }}>
            <div className="wb-col wb-gap-2">
              {/* Name the operator on the pass itself — a rider holding a Kochi ticket
                  should never have to infer whose network they are on. */}
              <span className="wb-eyebrow">
                {operatorLabel(operator) || (isBus ? 'Bus ticket' : 'Metro ticket')}
              </span>
              {ticket.ticketName && <span className="wb-strong">{ticket.ticketName}</span>}
            </div>
            <StatusBadge status={ticket.status} label={orderStatusLabel(ticket.status)} />
          </div>

          <JourneyLine
            from={ticket.journey.from.name}
            to={ticket.journey.to.name}
            accent={accent}
          />

          {passengerCount > 1 && (
            <div className="wb-row wb-gap-6" style={{ alignItems: 'center' }}>
              <Icon name="user" size={15} />
              <span className="wb-small wb-strong">
                {ticket.passes.length === 1
                  ? `${passengerCount} passengers · one QR for the group`
                  : `${passengerCount} passengers · one QR each`}
              </span>
            </div>
          )}

          <div className="wb-divider" />

          <div className="wb-col wb-gap-16">
            {ticket.passes.map((pass, i) => (
              <PassCard
                key={pass.fulfillmentId || i}
                pass={pass}
                labelMode={ticket.passes.length > 1 ? passLabelMode : null}
                index={i}
                total={ticket.passes.length}
                offsetMs={qrOffsetMs}
                cancelState={isCancelled ? 'cancelled' : isCancelling ? 'cancelling' : 'none'}
              />
            ))}
          </div>
        </div>

        {/* Ticket details (receipt) */}
        <Accordion title="Ticket details" defaultOpen>
          <div className="wb-col">
            {ticketTypeLabel(ticket.ticketType) && (
              <SumRow label="Journey type">{ticketTypeLabel(ticket.ticketType)}</SumRow>
            )}
            <SumRow label="Passengers">{passengerCount}</SumRow>
            <SumRow label="From">{ticket.journey.from.name}</SumRow>
            <SumRow label="To">{ticket.journey.to.name}</SumRow>
            {ticket.purchasedAt && (
              <SumRow label="Issued on">{formatDateTimeFull(ticket.purchasedAt)}</SumRow>
            )}
            {validUntil && <SumRow label="Valid until">{formatDateTimeFull(validUntil)}</SumRow>}
            {ticket.payment.transactionId && (
              <SumRow label="Payment ID">{ticket.payment.transactionId}</SumRow>
            )}
            {ticketNumbers && <SumRow label="Ticket no.">{ticketNumbers}</SumRow>}
            {ticket.becknOrderId && <SumRow label="Order ID">{ticket.becknOrderId}</SumRow>}
            <SumRow label="Payment status">
              {ticket.payment.status === 'PAID' ? 'Paid' : ticket.payment.status}
            </SumRow>
          </div>
        </Accordion>

        {/* Fare breakdown */}
        <div className="wb-card wb-card--pad wb-col wb-gap-8">
          <span className="wb-h3">Fare breakdown</span>
          <div className="wb-col">
            {fareLines.map((line, i) => (
              <SumRow key={i} label={line.label}>
                <Money v={line.value} />
              </SumRow>
            ))}
          </div>
          <div className="wb-divider" />
          <SumRow label="Amount paid" strong big>
            <Money v={ticket.payment.amount.value} strong />
          </SumRow>
        </div>

        {/* Terms & conditions — this operator's, or nothing. Never another operator's. */}
        {terms.length > 0 && (
          <Accordion title="Terms & conditions" subtitle={operatorLabel(operator) || undefined}>
            <ul className="wb-col wb-gap-8" style={{ margin: 0, paddingInlineStart: 18 }}>
              {terms.map((term, i) => (
                <li key={i} className="wb-small">
                  {term}
                </li>
              ))}
            </ul>
            {operator?.termsUrl && (
              <a
                className="wb-small wb-strong"
                href={operator.termsUrl}
                target="_blank"
                rel="noreferrer"
              >
                Read {operatorLabel(operator) || 'the operator'}'s full terms
              </a>
            )}
          </Accordion>
        )}

        {cancelError && (
          <p className="wb-small" style={{ color: 'var(--danger)' }}>
            {cancelError}
          </p>
        )}

        {/* Actions */}
        {isActive && (
          <div className="wb-row wb-wrap wb-gap-8">
            <Button variant="secondary" icon="alert" onClick={() => setReportOpen(true)}>
              Report a problem
            </Button>

            {canStartCancel && (
              <Button variant="danger" onClick={() => startCancel()}>
                Cancel ticket
              </Button>
            )}

            {cancelStage === 'soft' && (
              <span className="wb-row wb-gap-8 wb-small" style={{ alignItems: 'center' }}>
                <Spinner />
                Calculating refund…
              </span>
            )}
          </div>
        )}

        {/* Partial cancel: one leg of a round trip, or one passenger's ticket */}
        {ticket.passes.length > 1 && canStartCancel && (
          <div className="wb-col wb-gap-8">
            <p className="wb-tiny">
              {passLabelMode === 'leg'
                ? 'Cancel just one journey (refunds only that leg)'
                : "Cancel just one passenger's ticket (refunds only that ticket)"}
            </p>
            <div className="wb-row wb-wrap wb-gap-8">
              {ticket.passes.map((p, i) => (
                <Button
                  key={p.fulfillmentId}
                  variant="secondary"
                  size="sm"
                  onClick={() => startCancel(p.fulfillmentId)}
                >
                  {passLabelMode === 'leg'
                    ? `Cancel ${i === 0 ? 'onward' : 'return'} journey`
                    : `Cancel passenger ${i + 1}`}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Bus-only post-confirm flows (TRV11 2.0.1) */}
        {isBus && isActive && cancelStage === 'none' && (
          <div className="wb-card wb-card--pad wb-col wb-gap-12">
            <p className="wb-eyebrow">Manage this bus ticket</p>
            <InfoNote>
              Bus operators let you change your drop-off stop before you travel, or confirm which
              bus you boarded. These options aren’t available for metro tickets.
            </InfoNote>

            {/* Change destination */}
            {cdStage === 'none' && (
              <div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setCdError('');
                    setCdStage('form');
                  }}
                >
                  Change destination
                </Button>
              </div>
            )}
            {cdStage === 'form' && (
              <div className="wb-col wb-gap-8">
                <p className="wb-strong">New destination stop</p>
                <p className="wb-tiny">
                  Enter the new stop’s name. A stop code or GPS is optional, but helps the operator
                  match the exact stop. We’ll show the updated fare before you confirm.
                </p>
                <input
                  type="text"
                  value={cdName}
                  onChange={(e) => setCdName(e.target.value)}
                  placeholder="Stop name (required)"
                  className="wb-field"
                />
                <input
                  type="text"
                  value={cdCode}
                  onChange={(e) => setCdCode(e.target.value)}
                  placeholder="Stop code — optional"
                  className="wb-field"
                />
                <input
                  type="text"
                  value={cdGps}
                  onChange={(e) => setCdGps(e.target.value)}
                  placeholder="GPS — 'lat, lng' (optional)"
                  className="wb-field"
                />
                <div
                  className="wb-row wb-gap-8"
                  style={{ justifyContent: 'flex-end', paddingTop: 4 }}
                >
                  <Button variant="ghost" size="sm" onClick={() => setCdStage('none')}>
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={requestNewFare}
                    disabled={!cdName.trim() && !cdCode.trim()}
                  >
                    Request new fare
                  </Button>
                </div>
              </div>
            )}
            {(cdStage === 'soft' || cdStage === 'confirm') && (
              <span className="wb-row wb-gap-8 wb-small" style={{ alignItems: 'center' }}>
                <Spinner />
                {cdStage === 'soft' ? 'Requesting new fare…' : 'Confirming change…'}
              </span>
            )}
            {cdStage === 'preview' && (
              <div className="wb-col wb-gap-8">
                <p className="wb-body">
                  Updated fare: <span className="wb-strong">{formatMoney(ticket.quote.total)}</span>
                </p>
                <p className="wb-tiny">Any fare difference is settled per the operator's terms.</p>
                <div className="wb-row wb-gap-8" style={{ justifyContent: 'flex-end' }}>
                  <Button variant="ghost" size="sm" onClick={() => setCdStage('none')}>
                    Discard
                  </Button>
                  <Button variant="primary" size="sm" onClick={confirmChangeDest}>
                    Confirm change
                  </Button>
                </div>
              </div>
            )}
            {cdStage === 'done' && <p className="wb-small">Destination changed.</p>}
            {cdError && (
              <p className="wb-small" style={{ color: 'var(--danger)' }}>
                {cdError}
              </p>
            )}

            {/* Confirm vehicle (vehicle-based or user-based) */}
            {cdStage === 'none' &&
              (!cvOpen ? (
                <div>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setCvError('');
                      setCvOpen(true);
                    }}
                  >
                    Confirm vehicle
                  </Button>
                </div>
              ) : (
                <div className="wb-col wb-gap-8">
                  <p className="wb-strong">Confirm your boarding</p>
                  <p className="wb-tiny">
                    Some operators ask you to confirm the bus you boarded. Enter the vehicle’s
                    registration number, or tap “I’ve boarded” if your operator only needs a
                    confirmation.
                  </p>
                  <input
                    type="text"
                    value={cvReg}
                    onChange={(e) => setCvReg(e.target.value)}
                    placeholder="Vehicle registration (e.g. KA01AB1234)"
                    className="wb-field"
                  />
                  {cvError && (
                    <p className="wb-small" style={{ color: 'var(--danger)' }}>
                      {cvError}
                    </p>
                  )}
                  <div
                    className="wb-row wb-wrap wb-gap-8"
                    style={{ justifyContent: 'flex-end', paddingTop: 4 }}
                  >
                    <Button variant="ghost" size="sm" onClick={() => setCvOpen(false)}>
                      Cancel
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => void submitConfirmVehicle({ claimAuthorization: true })}
                      disabled={confirmVehicleMut.isPending}
                    >
                      I've boarded
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() =>
                        void submitConfirmVehicle({ vehicleRegistration: cvReg.trim() })
                      }
                      disabled={!cvReg.trim() || confirmVehicleMut.isPending}
                    >
                      {confirmVehicleMut.isPending ? 'Confirming…' : 'Confirm registration'}
                    </Button>
                  </div>
                </div>
              ))}
          </div>
        )}

        {/* Cancel confirmation dialog (opens once the SOFT refund preview lands) */}
        <ConfirmDialog
          open={cancelStage === 'preview' || cancelStage === 'confirm'}
          onClose={keepTicket}
          title={isBus ? 'Review bus ticket cancellation' : 'Cancel this ticket?'}
          confirmLabel={
            cancelStage === 'confirm'
              ? 'Cancelling…'
              : isBus
                ? 'Confirm cancellation'
                : 'Cancel ticket'
          }
          cancelLabel="Keep ticket"
          confirmVariant="danger"
          onConfirm={confirmCancel}
          busy={cancelStage === 'confirm'}
        >
          <div className="wb-col wb-gap-12">
            {isBus && cancelStage === 'preview' && (
              <InfoNote>
                This is a refund preview. Your bus ticket will be cancelled only after you confirm.
              </InfoNote>
            )}
            {/* Ticket summary — confirm you're cancelling the right ticket. */}
            <div
              className="wb-inset wb-col wb-gap-4"
              style={{ padding: 14, borderRadius: 'var(--r-md)' }}
            >
              <span className="wb-small">{ticket.provider.name}</span>
              <span className="wb-strong">
                {ticket.journey.from.name} → {ticket.journey.to.name}
              </span>
              <span className="wb-small">
                {ticket.ticketName || ticket.ticketType || 'Ticket'} · {passengerCount} passenger
                {passengerCount > 1 ? 's' : ''}
                {ticketNumbers ? ` · No. ${ticketNumbers}` : ''}
              </span>
            </div>

            {/* Refund breakdown. */}
            {ticket.cancellation && (
              <div className="wb-inset wb-col" style={{ padding: 14, borderRadius: 'var(--r-md)' }}>
                <SumRow label="Ticket fare">
                  <Money v={ticket.fare.value} />
                </SumRow>
                {/* Any extra priced lines the operator returned (e.g. TAX, CONVENIENCE_FEE). The
                    ledger lines BASE_FARE / REFUND / CANCELLATION_CHARGES are shown as their own rows. */}
                {(ticket.cancellation.breakup ?? [])
                  .filter(
                    (b) =>
                      !['BASE_FARE', 'REFUND', 'CANCELLATION_CHARGES'].includes(
                        (b.title || '').toUpperCase()
                      )
                  )
                  .map((b) => (
                    <SumRow key={b.title} label={fareLineLabel(b.title)}>
                      <Money v={b.amount.value} />
                    </SumRow>
                  ))}
                {ticket.cancellation.charges && (
                  <SumRow label="Cancellation charges">
                    <span className="wb-strong" style={{ color: 'var(--danger, #b3261e)' }}>
                      −<Money v={ticket.cancellation.charges.value} />
                    </span>
                  </SumRow>
                )}
                <div className="wb-divider" />
                <SumRow label="Refund amount" strong>
                  {/* An absent REFUND line means a FULL refund, not zero — see
                      lib/cancel-refund.ts. Rendering `?? '0'` told BMRCL riders
                      they would get nothing back on a full-refund cancellation. */}
                  <Money v={displayRefund(ticket.cancellation, ticket).value} strong />
                </SumRow>
                {Boolean(ticket.cancellation.refundPassengerCount) && (
                  <span className="wb-small" style={{ marginTop: 6 }}>
                    {cancelStage === 'preview' ? 'Refund quote covers' : 'Refund covers'}{' '}
                    {ticket.cancellation.refundPassengerCount} passenger ticket
                    {ticket.cancellation.refundPassengerCount === 1 ? '' : 's'}.
                  </span>
                )}
                <span className="wb-small" style={{ marginTop: 6 }}>
                  {ticket.cancellation.refundEta
                    ? `Refund ${ticket.cancellation.refundEta}.`
                    : `Refund to your original payment method within ${refundEta(operator)}.`}
                </span>
              </div>
            )}

            {/* This operator's window, or an honest "subject to the operator's window" —
                never another operator's number. */}
            <InfoNote tone="amber">{cancellationNote(operator)}</InfoNote>
          </div>
        </ConfirmDialog>

        {reportOpen && (
          <ReportProblemModal
            placeholder="Describe the issue with your ticket…"
            // Metro AND bus both run IGM 1.0.0 live (the TRV11-wide default, QA-confirmed) with the
            // same transit taxonomy. Bus IGM 1.0 requires a phone; email remains optional.
            // (The 2.0.0/ITM004 builder is exercised only by the cert harness.)
            categories={TRANSIT_ISSUE_CATEGORIES}
            requireEmail={!isBus}
            defaultEmail={auth.currentUser?.email ?? ''}
            requirePhone
            defaultPhone={contactPhone}
            uploader={ticketsApi.uploadIssueAttachment}
            onClose={() => setReportOpen(false)}
            onSubmit={async (description, extras) => {
              const txnId = ticket?.payment?.transactionId;
              if (txnId) {
                try {
                  await ticketsApi.status(txnId, id);
                  await new Promise((r) => setTimeout(r, 1200));
                } catch {
                  // status refresh is best-effort before opening issue
                }
              }
              const ack = await ticketsApi.raiseIssue(id, {
                description,
                igmVersion: isBus ? '1.0.0' : undefined,
                subCategory: extras.subCategory,
                contactPhone: extras.contactPhone,
                contactEmail: extras.contactEmail,
                images: extras.images,
              });
              setReportOpen(false);
              void navigate({
                to: '/support/$category/$issueId',
                params: { category: isBus ? 'bus' : 'metro', issueId: ack.issueId },
              });
            }}
          />
        )}
      </div>
    </Screen>
  );
}

// cancelErrorMessage turns backend cancel failures into something a rider understands.
// ONDC_012/ONDC_023 are state-transition failures that mean the order is already
// mid-cancel/cancelled for this stage.
function cancelErrorMessage(e: unknown): string {
  const err = e as { status?: number; code?: string } | null;
  if (err?.status === 409 || err?.code === 'ONDC_012' || err?.code === 'ONDC_023') {
    return "This ticket can't be cancelled right now — it may already be cancelling or cancelled. Pull to refresh for the latest status.";
  }
  return getErrorMessage(e);
}

// buildFareLines renders the fare breakdown ("N passengers", GST, …). Prefers the operator's quote
// breakup (humanized), collapsing the base-fare line into the passenger count; falls back to a
// single passenger line at the paid amount when no breakup is present.
function buildFareLines(
  ticket: Ticket,
  passengerCount: number
): { label: string; value: string }[] {
  const paxLabel = `${passengerCount} passenger${passengerCount > 1 ? 's' : ''}`;
  const breakup = ticket.quote?.breakup ?? [];
  if (breakup.length === 0) {
    return [{ label: paxLabel, value: ticket.payment.amount.value }];
  }
  return breakup.map((b) => {
    const isBase = b.title === 'BASE_FARE' || b.title === 'BASE_PRICE';
    return { label: isBase ? paxLabel : fareLineLabel(b.title), value: b.amount.value };
  });
}

function fareLineLabel(title: string): string {
  switch (title) {
    case 'TAX':
      return 'GST';
    case 'TOLL':
      return 'Toll';
    case 'OFFER':
      return 'Discount';
    case 'CANCELLATION_CHARGES':
      return 'Cancellation charges';
    case 'REFUND':
      return 'Refund';
    default:
      return title
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/^\w/, (c) => c.toUpperCase());
  }
}

// CancelStatusBanner is the calm, reassuring status card shown while a ticket is cancelling or after
// it's cancelled — refund amount + timeline, plus the "don't use it" note while still in progress.
function CancelStatusBanner({ ticket }: { ticket: Ticket }) {
  const cancelled =
    ticket.status === 'CANCELLED' ||
    ticket.cancellation?.status === 'CANCELLED' ||
    ticket.cancellation?.state === 'cancelled';
  // displayRefund, NOT cancellation.refund — for the same reason the cancel dialog uses it: BMRCL
  // quotes a bare cancellation with no REFUND line, which the backend treats as a FULL refund of the
  // captured amount. Reading the raw field left `refund` undefined and the guard below suppressed the
  // sentence entirely, so a cancelled BMRCL ticket told the rider their ticket was gone and NOTHING
  // about their money. Silence is the worst of the three options here.
  const refund = displayRefund(ticket.cancellation, ticket);
  // Only a value that parses cleanly to zero counts as "nothing back". An unparseable amount must
  // fall through to showing the figure — never to silently claiming no refund is owed.
  const refundAmount = Number(refund.value);
  const noRefundDue = Number.isFinite(refundAmount) && refundAmount === 0;
  return (
    <div
      className="wb-card wb-card--pad wb-col wb-gap-8"
      style={{
        background: 'color-mix(in oklab, var(--danger) 8%, var(--surface))',
        borderColor: 'color-mix(in oklab, var(--danger) 22%, transparent)',
      }}
    >
      <div className="wb-row wb-gap-8" style={{ alignItems: 'center' }}>
        <Icon name="alert" size={18} style={{ color: 'var(--danger)' }} />
        <span className="wb-strong">
          {cancelled ? 'Ticket cancelled' : 'Cancellation in progress'}
        </span>
      </div>
      {noRefundDue ? (
        // An EXPLICIT zero is a real outcome (a 100% cancellation fee), and the rider has to be told
        // plainly rather than left to infer it from a missing sentence.
        <p className="wb-small">No refund is due on this cancellation.</p>
      ) : (
        <p className="wb-small">
          A refund of {formatMoney(refund)} will be credited to your original payment method within{' '}
          {refundEta(ticket.operator)}.
        </p>
      )}
      {!cancelled && (
        <p className="wb-tiny">
          Don’t use your ticket after requesting cancellation — a used ticket can’t be refunded.
        </p>
      )}
    </div>
  );
}

function PartialCancelStatusBanner({
  ticket,
  passengerCount,
}: {
  ticket: Ticket;
  passengerCount: number;
}) {
  const refundedCount = ticket.cancellation?.refundPassengerCount ?? 0;
  const remaining = Math.max(passengerCount - refundedCount, 0);
  const refund = displayRefund(ticket.cancellation, ticket);
  return (
    <div
      className="wb-card wb-card--pad wb-col wb-gap-8"
      style={{
        background: 'color-mix(in oklab, var(--success) 10%, var(--surface))',
        borderColor: 'color-mix(in oklab, var(--success) 28%, transparent)',
      }}
    >
      <div className="wb-row wb-gap-8" style={{ alignItems: 'center' }}>
        <Icon name="info" size={18} />
        <span className="wb-strong">Partial cancellation complete</span>
      </div>
      <p className="wb-small">
        {refundedCount} of {passengerCount} passenger ticket
        {passengerCount === 1 ? '' : 's'} {refundedCount === 1 ? 'was' : 'were'} refunded
        {remaining > 0
          ? `; ${remaining} passenger${remaining === 1 ? '' : 's'} still travelling.`
          : '.'}
      </p>
      <p className="wb-small">
        A refund of {formatMoney(refund)} will be credited within {refundEta(ticket.operator)}.
      </p>
      {remaining > 0 && (
        <p className="wb-tiny">
          Keep this QR available. It remains valid for passengers already inside the metro network.
        </p>
      )}
    </div>
  );
}

function PassCard({
  pass,
  labelMode,
  index,
  total,
  offsetMs,
  cancelState,
}: {
  pass: TicketPass;
  labelMode: 'leg' | 'passenger' | null;
  index: number;
  total: number;
  offsetMs: number;
  // Whether the ORDER is cancelling/cancelled. A pass cannot work this out for itself: the backend
  // withholds the QR the moment a confirm-cancel is sent, and pass.ticketStatus stays UNCLAIMED
  // throughout — so without this the card reads a withheld QR as "not ready yet" and keeps printing
  // "Ready to use" on a ticket that is being, or has been, cancelled.
  cancelState: 'none' | 'cancelling' | 'cancelled';
}) {
  const isBus = pass.journey.vehicleCategory === 'BUS';
  const isDynamic = Boolean(pass.qrDynamic && pass.qrStaticBlock);
  const hasQr = Boolean(pass.qrImage) || isDynamic;
  const cancelBlocked = cancelState !== 'none';
  // Suppress the QR for a cancelling/cancelled order even on the off-chance one is still present:
  // the ticket must stop looking scannable the moment the rider commits to cancelling it.
  const blocked = pass.ticketStatus === 'CLAIMED' || cancelBlocked;
  const label =
    labelMode === 'leg'
      ? index === 0
        ? 'Onward journey'
        : 'Return journey'
      : labelMode === 'passenger'
        ? `Passenger ${index + 1} of ${total}`
        : null;
  return (
    <div className="wb-col wb-center wb-gap-12" style={{ textAlign: 'center' }}>
      {label && <p className="wb-tiny wb-strong">{label}</p>}
      <div style={{ width: '100%', maxWidth: 280 }}>
        {blocked ? (
          <div className="wb-inset wb-center" style={{ height: 200, color: 'var(--ink-3)' }}>
            <Icon name="checkCircle" size={40} />
          </div>
        ) : isDynamic ? (
          <QrFrame live refreshSeconds={pass.qrRefreshSeconds ?? 10}>
            <DynamicQRCode
              staticBlock={pass.qrStaticBlock ?? ''}
              lat={pass.qrLat ?? ''}
              lng={pass.qrLng ?? ''}
              refreshSeconds={pass.qrRefreshSeconds ?? 10}
              offsetMs={offsetMs}
            />
          </QrFrame>
        ) : pass.qrImage ? (
          <QrFrame>
            <img
              src={pass.qrImage}
              alt="Your ticket QR code"
              style={{ width: '100%', height: 'auto' }}
            />
          </QrFrame>
        ) : (
          <div className="wb-inset wb-center" style={{ height: 200, color: 'var(--ink-3)' }}>
            <Icon name="qr" size={40} />
          </div>
        )}
      </div>
      <p className="wb-small wb-strong" style={{ color: 'var(--ink)' }}>
        {cancelState === 'cancelled'
          ? 'This ticket was cancelled. The QR no longer works.'
          : cancelState === 'cancelling'
            ? 'Cancellation in progress — this QR can no longer be used.'
            : blocked
              ? 'This ticket has been used.'
              : hasQr
                ? isBus
                  ? 'Show this QR to the conductor when you board.'
                  : 'Scan this QR at the metro gate to enter and exit.'
                : 'Your ticket QR will appear here shortly.'}
      </p>
      {/* pass.ticketStatus is the CREDENTIAL's state and stays UNCLAIMED through a cancellation, so
          rendering it here printed "Ready to use" directly beneath the cancellation warning. The
          order's state overrides it — a cancelled ticket is not usable, whatever the pass says. */}
      {cancelBlocked ? (
        <StatusBadge
          kind="credential"
          status="CLAIMED"
          label={cancelState === 'cancelled' ? 'Cancelled' : 'Cancelling'}
        />
      ) : (
        pass.ticketStatus && (
          <StatusBadge
            kind="credential"
            status={pass.ticketStatus}
            label={ticketStatusLabel(pass.ticketStatus)}
          />
        )
      )}
    </div>
  );
}
